import { createApi } from '@reduxjs/toolkit/dist/query/react'

import {
  CreateMemberPayload,
  Member,
  Organization,
  OrganizationPayload,
  OrganizationWalletsFilter,
  TPermissions,
} from 'types/organization'
import {
  MembersListResponse,
  TemplateDefaultListResponse,
  TemplateListRequestResponse,
  WalletListResponse,
} from 'types/responses'
import { PSDefaultTemplate, PSTemplateRequest } from 'types/template'
import { NewWallet, Wallet } from 'types/wallet'

import { addSessionId } from 'utils/uuid'

import baseQuery from '../api'

export const organizationApi = createApi({
  reducerPath: 'organization',
  baseQuery,
  tagTypes: ['Organization', 'Member'],
  endpoints: (build) => ({
    createOrganization: build.mutation<void, OrganizationPayload>({
      query: (body) => ({
        url: '/organization',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Organization'],
    }),
    editOrganization: build.mutation<void, { organizationId: string; name: string }>({
      query: ({ organizationId, name }) => ({
        url: `/organization/${organizationId}/edit`,
        method: 'POST',
        body: { name },
      }),
      invalidatesTags: ['Organization'],
    }),
    getOrganizationsList: build.query<Organization[], void>({
      query: () => ({
        url: '/user/profile/organizations',
        method: 'GET',
      }),
      providesTags: ['Organization'],
      transformResponse: (response: { list: Organization[] }) => response.list,
    }),
    getOrganizationById: build.query<Organization, string>({
      query: (id) => ({
        url: `/organization/${id}`,
        method: 'GET',
      }),
      providesTags: ['Organization'],
    }),

    // =================== Members ===================

    getOrganizationMembers: build.query<Member[], string>({
      query: (organizationId) => ({
        url: `/organization/${organizationId}/members`,
        method: 'GET',
      }),
      providesTags: ['Member'],
      transformResponse: (response: MembersListResponse) => response.members,
    }),
    createOrganizationMember: build.mutation<void, CreateMemberPayload>({
      query: (body) => ({
        url: `/organization/member`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Member'],
    }),
    updateOrganizationMember: build.mutation<
      void,
      { memberId: string; permissions: TPermissions[] }
    >({
      query: ({ memberId, permissions }) => ({
        url: `/organization/member/${memberId}/permissions`,
        method: 'POST',
        body: { permissions },
      }),
      invalidatesTags: ['Member'],
    }),
    removeOrganizationMember: build.mutation<void, string>({
      query: (memberId) => ({
        url: `/organization/member/${memberId}/remove`,
        method: 'POST',
      }),
      invalidatesTags: ['Member'],
    }),

    // =================== templates ===================

    getOrganizationTemplatesDefault: build.query<PSDefaultTemplate[], string>({
      query: (organizationId) => ({
        url: `/organization/${organizationId}/withdraw/templates/default`,
        method: 'GET',
      }),
      transformResponse: (response: TemplateDefaultListResponse) => response.list,
    }),
    getOrganizationTemplatesSaved: build.query<PSTemplateRequest[], string>({
      query: (organizationId) => ({
        url: `/organization/${organizationId}/withdraw/templates/saved`,
        method: 'GET',
      }),
      transformResponse: (response: TemplateListRequestResponse) => response.list,
    }),
    createOrganizationTemplate: build.mutation<
      void,
      { organizationId: string; template: PSTemplateRequest }
    >({
      query: ({ organizationId, template }) => ({
        url: `/organization/${organizationId}/withdraw/templates/create`,
        method: 'POST',
        body: addSessionId(template),
      }),
    }),
    editOrganizationTemplate: build.mutation<
      void,
      { organizationId: string; template: PSTemplateRequest }
    >({
      query: ({ organizationId, template }) => ({
        url: `/organization/${organizationId}/withdraw/templates/`,
        method: 'POST',
        body: addSessionId(template),
      }),
    }),
    deleteOrganizationTemplate: build.mutation<
      void,
      { organizationId: string; templateId: string }
    >({
      query: ({ organizationId, templateId }) => ({
        url: `/organization/${organizationId}/withdraw/templates/${templateId}/delete`,
        method: 'POST',
        body: addSessionId(),
      }),
    }),

    // =================== wallets ===================

    getOrganizationWallets: build.query<
      Wallet[],
      { organizationId: string; body: OrganizationWalletsFilter }
    >({
      query: ({ organizationId, body }) => ({
        url: `/organization/${organizationId}/wallets`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: WalletListResponse) => response.list,
    }),
    createOrganizationWallet: build.mutation<Wallet[], { organizationId: string; body: NewWallet }>(
      {
        query: ({ organizationId, body }) => ({
          url: `/organization/${organizationId}/wallets/add`,
          method: 'POST',
          body,
        }),
        transformResponse: (response: WalletListResponse) => response.list,
      }
    ),
  }),
})

export const {
  useCreateOrganizationMutation,
  useEditOrganizationMutation,
  useGetOrganizationsListQuery,
  useGetOrganizationByIdQuery,

  useGetOrganizationMembersQuery,
  useCreateOrganizationMemberMutation,
  useUpdateOrganizationMemberMutation,
  useRemoveOrganizationMemberMutation,

  useGetOrganizationTemplatesDefaultQuery,
  useGetOrganizationTemplatesSavedQuery,
  useCreateOrganizationTemplateMutation,
  useEditOrganizationTemplateMutation,
  useDeleteOrganizationTemplateMutation,

  useGetOrganizationWalletsQuery,
  useCreateOrganizationWalletMutation,
} = organizationApi
